import React, { useRef, useState } from 'react';
import { ImageUpIcon, RefreshCwIcon, Trash2Icon, UploadCloudIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { uploadBrandAsset } from '../../services/fileService';
interface LogoUploaderProps {
  value?: string;
  onChange: (value?: string) => void;
  label?: string;
  hint?: string;
  /** Si se indica, el archivo se sube a Supabase Storage. */
  organizationId?: string;
  kind?: 'logo' | 'favicon';
}
const ACCEPTED = ['image/png', 'image/svg+xml', 'image/webp', 'image/jpeg'];

/**
 * Carga de logotipo con arrastrar y soltar, vista previa y borrado.
 * Cuando se conoce la organización, el archivo se sube al bucket público
 * correspondiente con la ruta `organization_id/archivo` y se guarda su URL.
 */
export function LogoUploader({
  value,
  onChange,
  label = 'Logotipo de la empresa',
  hint = 'PNG, SVG, WEBP o JPG · máximo 2 MB',
  organizationId,
  kind = 'logo'
}: LogoUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function handleFile(file?: File) {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      setError('Formato no admitido. Usa PNG, SVG, WEBP o JPG.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('El archivo supera los 2 MB.');
      return;
    }
    setError(null);
    if (organizationId) {
      try {
        onChange(await uploadBrandAsset(organizationId, file, kind));
        return;
      } catch {
        setError('No hemos podido subir el archivo. Inténtalo de nuevo.');
        return;
      }
    }

    // Sin organización todavía (asistente de registro): vista previa local.
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.readAsDataURL(file);
  }
  return <div className="space-y-3" data-id="element-847" data-mp="1ezw7gdpfmulj">
      <p className="text-sm font-medium text-foreground" data-id="element-848" data-mp="1f0npelq3wttc">{label}</p>

      {value ? <div className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-surface p-6 sm:flex-row sm:items-center" data-id="element-849" data-mp="1m3c823veadp1">
          <div className="flex h-24 w-40 items-center justify-center rounded-xl border border-border bg-background p-3" data-id="element-850" data-mp="1r1x8a3ancxdh">
            <img src={value} alt="Vista previa del logotipo" className="max-h-full max-w-full object-contain" data-id="element-851" data-mp="1vyvywbv3qm6x" />
          </div>
          <div className="flex flex-1 flex-col gap-2 sm:flex-row" data-id="element-852" data-mp="24tk5q6gbsbxu">
            <Button variant="secondary" size="sm" leftIcon={<RefreshCwIcon className="h-4 w-4" data-id="element-854" data-mp="29r3tbwywy12g" />} onClick={() => inputRef.current?.click()} data-id="element-853" data-mp="24vephhfv1y0c">
              Cambiar
            </Button>
            <Button variant="ghost" size="sm" leftIcon={<Trash2Icon className="h-4 w-4" data-id="element-856" data-mp="2jqups471aq56" />} onClick={() => onChange(undefined)} data-id="element-855" data-mp="2erzz6g2trxai">
              Eliminar
            </Button>
          </div>
        </div> : <button type="button" onClick={() => inputRef.current?.click()} onDragOver={e => {
      e.preventDefault();
      setDragging(true);
    }} onDragLeave={() => setDragging(false)} onDrop={e => {
      e.preventDefault();
      setDragging(false);
      handleFile(e.dataTransfer.files?.[0]);
    }} className={`flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-colors ${dragging ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:border-primary/50'}`} data-id="element-857" data-mp="kf1mjptnkok0">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background shadow-sm" data-id="element-858" data-mp="3llxolzierkla">
            {dragging ? <UploadCloudIcon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-859" data-mp="3hl5fr7dg7hpj" /> : <ImageUpIcon className="h-5 w-5 text-muted-foreground" aria-hidden="true" data-id="element-860" data-mp="3ck7xkusfuno1" />}
          </span>
          <span className="text-sm font-medium text-foreground" data-id="element-861" data-mp="37mfvx91zrn6n">
            Arrastra tu logotipo o haz clic para subirlo
          </span>
          <span className="text-xs text-muted-foreground" data-id="element-862" data-mp="216vr0c5anqy6">{hint}</span>
        </button>}

      {error && <p role="alert" className="text-sm font-medium text-red-600" data-id="element-863" data-mp="1w4wp76p7d23c">
          {error}
        </p>}

      <input ref={inputRef} type="file" accept={ACCEPTED.join(',')} className="sr-only" onChange={e => handleFile(e.target.files?.[0])} data-id="element-864" data-mp="3k1ma13fopnmb" />
    </div>;
}